import { Button, Intent } from "@blueprintjs/core";
import { ButtonContainer, FGCustomPanel, FGSelectInput, FieldGroup, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";

import {
  ERequestState,
  LinkedRequestsApi,
  LinkedRequestsSearch,
  ReferentialApi,
  RequestApi,
  RequestGridDto,
  RequestInfoDto,
} from "../api";
import { EditButton, SearchTablePage, SmallFormGenerator, ViewButton } from "../components";
import { useGlobalData } from "../contexts";
import { useApiService, useTl } from "../hooks";
import { useReferential } from "../hooks/useReferential";
import { ETLCodes } from "../locales";
import { getRouteStr } from "../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../utils/RequestSearchRouteUtil";

export interface ILinkedSubFilesTabProps {
  requestInfo: RequestInfoDto;
  editMode: boolean;
}

const Container = styled.div`
  padding: 0.5rem;
  padding-bottom: 3px;
  vertical-align: middle;
  z-index: 10;
  display: flex;
  flex-direction: row;
`;

const StyledButton = styled(Button)`
  float: left;
`;

export const LinkedSubFilesTab: React.FunctionComponent<ILinkedSubFilesTabProps> = ({ requestInfo, editMode }) => {
  const { t } = useTl();
  const api = useApiService(LinkedRequestsApi);
  const requestApi = useApiService(RequestApi);
  const referentialApi = useApiService(ReferentialApi);
  const history = useHistory();
  const { currentLanguage } = useGlobalData();

  const editableStatus = React.useMemo(
    () =>
      requestInfo.requestStateCode === ERequestState.REQUESTED &&
      (requestInfo?.masterRequestStateCode === ERequestState.REQUESTED || requestInfo?.masterRequestStateCode == null),
    [requestInfo]
  );

  const blocked = React.useMemo(() => requestInfo.isRequestBlocked, [requestInfo.isRequestBlocked]);

  const redirectToRequest = React.useCallback(
    async (row: RequestGridDto) => {
      const subRequestId = await requestApi.getSubRequestTypeId(row.requestId, row.requestTypeCode);
      const p = await getRouteStr(row.requestTypeCode, subRequestId, "edit");
      history.push(p);
    },
    [history, requestApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: RequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => window.open("/#" + getRouteStr(row.requestTypeCode, row.subRequestId, "view"))}
            />
            <EditButton
              minimal={true}
              disabled={blocked}
              onClick={() => window.open("/#" + getRouteStr(row.requestTypeCode, row.subRequestId, "edit"))}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: RequestGridDto) => (
          <Link to="_" onClick={() => redirectToRequest(row)}>
            {row.requestNumber}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.RequestType),
        fieldName: "requestType",
        render: (row: RequestGridDto) => (
          <Link target="_blank" to={getSearchRouteStr(row.requestTypeCode)}>
            {row.requestType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
    ],
    [blocked, t]
  );

  const searchFunction = React.useCallback(
    (sObj) =>
      api.baseSearch(
        LinkedRequestsSearch.fromJS({
          ...sObj,
          masterRequestId: requestInfo.parentRequestId,
          languageId: currentLanguage.languageId,
        })
      ),
    [api, currentLanguage.languageId, requestInfo.parentRequestId]
  );

  const [listSubFilesRequestTypes, setListSubFilesRequestTypes] = React.useState([]);

  const refSubFilesRequestTypes = React.useMemo(() => {
    const refResult = referentialApi.getSubFilesRequestTypes(
      requestInfo.requestTypeCode,
      currentLanguage.languageId,
      requestInfo.parentRequestId
    );
    refResult.then((list) => setListSubFilesRequestTypes(list));
    return refResult;
  }, [currentLanguage.languageId, referentialApi, requestInfo.parentRequestId, requestInfo.requestTypeCode]);

  const [subFilesRequestTypes, subFilesRequestTypesLoading] = useReferential(
    (refApi) => refSubFilesRequestTypes,
    true,
    [currentLanguage.languageId]
  );

  return (
    <>
      <Container>
        <div style={{ width: "65rem", marginLeft: "10px" }}>
          <SmallFormGenerator
            editMode={editMode}
            loading={subFilesRequestTypesLoading}
            hideButtons={true}
            initialValues={{ subFileRequestType: null }}
            enableDirtyCheck={false}
            onSubmit={(values) => {
              const subFileRequestType = listSubFilesRequestTypes.filter(
                (rt) => +rt.idValue === +values.subFileRequestType
              )[0];
              history.push(getRouteStr(subFileRequestType.keyValue, 0, "edit") + "/" + requestInfo.parentRequestId);
            }}
          >
            <FieldGroup columns={[5, 3, 2, 2]}>
              {editableStatus && !blocked && (
                <FGSelectInput
                  name="subFileRequestType"
                  items={subFilesRequestTypes}
                  loading={subFilesRequestTypesLoading}
                />
              )}
              {editableStatus && !blocked && (
                <FGCustomPanel>
                  {(ctx) => (
                    <ButtonContainer>
                      <StyledButton
                        text={t(ETLCodes.AddSubFile)}
                        intent={Intent.PRIMARY}
                        type="submit"
                        disabled={ctx.formik.values?.subFileRequestType == null}
                      />
                    </ButtonContainer>
                  )}
                </FGCustomPanel>
              )}
            </FieldGroup>
          </SmallFormGenerator>
        </div>
      </Container>

      <SearchTablePage
        columns={columns}
        searchFunction={searchFunction}
        sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      />
    </>
  );
};
